/**
 * StatusDot — Animated health/status indicator
 * Used across: Connections, Services, Models, Agents
 */

import { cn } from '@/lib/utils';

interface StatusDotProps {
  status: 'healthy' | 'degraded' | 'down' | 'unknown' | 'pending' | 'active' | 'inactive';
  size?: 'sm' | 'md' | 'lg';
  pulse?: boolean;
  className?: string;
}

const STATUS_COLORS: Record<string, string> = {
  healthy: 'bg-emerald-500',
  active: 'bg-emerald-500',
  degraded: 'bg-amber-500',
  pending: 'bg-amber-500',
  down: 'bg-red-500',
  inactive: 'bg-zinc-400',
  unknown: 'bg-zinc-400',
};

const SIZES = {
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
};

export function StatusDot({ status, size = 'md', pulse, className }: StatusDotProps) {
  const shouldPulse = pulse ?? (status === 'healthy' || status === 'active');

  return (
    <span className={cn('relative inline-flex', className)}>
      {shouldPulse && (
        <span className={cn(
          'absolute inline-flex rounded-full opacity-75 animate-ping',
          SIZES[size],
          STATUS_COLORS[status],
        )} />
      )}
      <span className={cn(
        'relative inline-flex rounded-full',
        SIZES[size],
        STATUS_COLORS[status],
      )} />
    </span>
  );
}
